import generatedTranslations from './translations.generated.json';

const textNodeState = new WeakMap();
const attributeState = new WeakMap();
const templateCache = new Map();
const lowercaseCatalogCache = new Map();
const catalogCache = new Map();
const TRANSLATABLE_ATTRIBUTES = ['aria-label', 'alt', 'placeholder', 'title'];
const SKIP_SELECTOR = 'script, style, noscript, code, pre, textarea, [contenteditable="true"], [data-i18n-ignore]';

export const normalizeSourceText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const getCatalog = (language) => {
  if (catalogCache.has(language)) return catalogCache.get(language);
  const stored = generatedTranslations?.[language];
  const catalog = Array.isArray(stored) && Array.isArray(generatedTranslations?._sources)
    ? Object.fromEntries(generatedTranslations._sources.map((source, index) => [source, stored[index]]))
    : (stored || {});
  catalogCache.set(language, catalog);
  return catalog;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getTemplateTranslations = (language) => {
  if (templateCache.has(language)) return templateCache.get(language);
  const catalog = getCatalog(language);
  const templates = Object.entries(catalog).flatMap(([source, translated]) => {
    if (!/\{\d+\}/.test(source)) return [];
    const tokens = [...source.matchAll(/\{(\d+)\}/g)];
    let cursor = 0;
    let pattern = '^';
    tokens.forEach((token) => {
      pattern += escapeRegExp(source.slice(cursor, token.index));
      pattern += '(.+?)';
      cursor = token.index + token[0].length;
    });
    pattern += `${escapeRegExp(source.slice(cursor))}$`;
    const specificity = source.replace(/\{\d+\}/g, '').length;
    return specificity >= 6 ? [{ regex: new RegExp(pattern), translated, specificity }] : [];
  }).sort((left, right) => right.specificity - left.specificity);
  templateCache.set(language, templates);
  return templates;
};

const getLowercaseCatalog = (language) => {
  if (lowercaseCatalogCache.has(language)) return lowercaseCatalogCache.get(language);
  const lookup = new Map();
  Object.entries(getCatalog(language)).forEach(([source, translated]) => {
    const key = source.toLocaleLowerCase();
    if (!lookup.has(key)) lookup.set(key, translated);
  });
  lowercaseCatalogCache.set(language, lookup);
  return lookup;
};

const preserveOuterWhitespace = (source, translated) => {
  const leading = source.match(/^\s*/)?.[0] || '';
  const trailing = source.match(/\s*$/)?.[0] || '';
  return `${leading}${translated}${trailing}`;
};

export const translateSourceText = (language, source) => {
  if (language === 'en') return source;
  const normalized = normalizeSourceText(source);
  if (!normalized) return source;
  const catalog = getCatalog(language);
  const exact = catalog[normalized];
  if (exact) return exact;

  for (const template of getTemplateTranslations(language)) {
    const match = normalized.match(template.regex);
    if (!match) continue;
    return template.translated.replace(/\{(\d+)\}/g, (_, index) => {
      const value = match[Number(index) + 1] || '';
      const normalizedValue = normalizeSourceText(value);
      return catalog[normalizedValue]
        || getLowercaseCatalog(language).get(normalizedValue.toLocaleLowerCase())
        || value;
    });
  }
  return source;
};

const shouldSkipElement = (element) => Boolean(element?.closest?.(SKIP_SELECTOR));

const translateTextNode = (node, language) => {
  if (!node?.parentElement || shouldSkipElement(node.parentElement)) return;

  const current = node.nodeValue || '';
  const previous = textNodeState.get(node);
  const source = previous && current === previous.translated ? previous.source : current;
  const translatedSource = translateSourceText(language, source);
  const translated = translatedSource === source ? source : preserveOuterWhitespace(source, translatedSource);

  textNodeState.set(node, { source, translated });
  if (current !== translated) node.nodeValue = translated;
};

const translateElementAttributes = (element, language) => {
  if (!element?.getAttribute || shouldSkipElement(element)) return;

  const saved = attributeState.get(element) || {};
  TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    const current = element.getAttribute(attribute) || '';
    const previous = saved[attribute];
    const source = previous && current === previous.translated ? previous.source : current;
    const translated = translateSourceText(language, source);
    saved[attribute] = { source, translated };
    if (current !== translated) element.setAttribute(attribute, translated);
  });
  attributeState.set(element, saved);
};

export const translateDomTree = (root, language) => {
  if (!root || typeof document === 'undefined') return;

  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root, language);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) translateElementAttributes(root, language);

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) translateTextNode(node, language);
    else translateElementAttributes(node, language);
    node = walker.nextNode();
  }
};

export const observeLocalizedDom = (language) => {
  if (typeof document === 'undefined' || !document.body) return () => {};

  let frame = null;
  const pendingRoots = new Set();
  const flush = () => {
    frame = null;
    pendingRoots.forEach((root) => translateDomTree(root, language));
    pendingRoots.clear();
  };
  const schedule = (root) => {
    if (root) pendingRoots.add(root);
    if (frame === null) frame = window.requestAnimationFrame(flush);
  };

  translateDomTree(document.body, language);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'characterData') schedule(mutation.target);
      mutation.addedNodes.forEach((node) => schedule(node));
    });
  });
  observer.observe(document.body, { childList: true, characterData: true, subtree: true });

  return () => {
    observer.disconnect();
    if (frame !== null) window.cancelAnimationFrame(frame);
  };
};
